import { useEffect } from 'react';
import { getTodosInDateRange, getTodosInDateRangeFromList } from '../../../services/todo/storage';
import { toCalendarDateKey } from '../shared/utils';
import type { Todo } from '@ext-bookmark/shared/types';

/**
 * 整块加载（loadMonthTodos）的合法触发条件（单向数据流）：
 * (1) 初始挂载 (2) 换月 currentDate 变化 (3) 筛选 selectedGroupIds/personalGroupId 变化 (4) todosSource 引用变化。
 * 不依赖 dataVersion，避免用户修改后 setDataVersion 触发整月重载。
 * 单条完成切换/增删改成功路径不得调用本 hook 的 loadMonthTodos；局部更新由 setMonthTodos(仅更新受影响日期) 或 onTodoUpdated/setQueryData 完成。
 */
export const useCalendarData = (
  currentDate: Date,
  selectedGroupIds: Set<string>,
  setMonthTodos: (todos: Map<string, Todo[]>) => void,
  personalGroupId?: string | null,
  _dataVersion?: number,
  /** 直接使用 React Query 的待办列表，避免依赖 localStorage 同步时机（桌面端与右侧面板数据一致） */
  todosSource?: Todo[] | null,
  /** 若存在且当前时间小于 ref.current，则跳过本次由 todosSource 变化触发的整月重载（避免勾选后整表闪烁） */
  skipTodosReloadUntilRef?: { current: number }
) => {
  const loadMonthTodos = async () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);

    // 优先使用传入的 todosSource（与右侧面板同源），否则从 localStorage 读
    let monthTodosList: Todo[];
    if (todosSource != null) {
      monthTodosList = getTodosInDateRangeFromList(todosSource, startDate, endDate);
    } else {
      monthTodosList = await getTodosInDateRange(startDate, endDate);
    }
    
    // 应用分组筛选（与右侧面板 getFilteredTodos 逻辑一致：个人/未分组任务当个人分组被选中时保留）
    if (selectedGroupIds && selectedGroupIds.size > 0) {
      monthTodosList = monthTodosList.filter(todo => {
        if (!todo.groupId) {
          return personalGroupId != null && selectedGroupIds.has(personalGroupId);
        }
        return selectedGroupIds.has(todo.groupId);
      });
    } else {
      // 如果没有选中任何分组，返回空数组
      monthTodosList = [];
    }
    
    // 按日期分组（使用 YYYY-MM-DD 避免 toDateString 因 locale 导致 key 不一致）
    const todosMap = new Map<string, Todo[]>();
    monthTodosList.forEach((todo) => {
      const dateKey = toCalendarDateKey(todo.startDate);
      if (!todosMap.has(dateKey)) {
        todosMap.set(dateKey, []);
      }
      todosMap.get(dateKey)!.push(todo);
    });
    
    setMonthTodos(todosMap);
  };

  // 加载当前月份的待办事项（勾选后 skip 窗口内不因 todosSource 变化重载整月；不依赖 dataVersion，保持单向数据流）
  useEffect(() => {
    if (skipTodosReloadUntilRef?.current && Date.now() < skipTodosReloadUntilRef.current) {
      return;
    }
    loadMonthTodos();
  }, [currentDate, selectedGroupIds, personalGroupId, todosSource]);

  return { loadMonthTodos };
};
